import { useEffect, useRef } from 'react';

import { useWidgetParam } from '@src/core/hooks';
import { useConfettiBurst } from '@src/common/hooks/useConfettiBurst';
import useModalOverlayLock from '@src/hooks/useModalOverlayLock';
import { track } from '@src/services/analytics';

import { CASHBACK_CONFETTI_DELAY_MS } from '../../constants';
import { buildCashbackWhatsappUrl } from '../../core/cashbackWhatsapp';

export type UseCashbackGrowthModalParams = {
  open: boolean;
  orderCode: string;
  amountCents: number;
  buyerFirstName?: string;
  onDismiss: () => void;
};

export const useCashbackGrowthModal = (params: UseCashbackGrowthModalParams) => {
  const { open, orderCode, amountCents, buyerFirstName, onDismiss } = params;
  const sellerName = useWidgetParam('business-name') ?? '';
  const panelRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const { fireConfetti } = useConfettiBurst({ wrapId: 'cashback-growth-confetti' });

  useModalOverlayLock(open);

  useEffect(() => {
    if (open) {
      track('cashbackGrowthModalView', { orderCode });
    }
  }, [open, orderCode]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const timeout = window.setTimeout(() => {
      fireConfetti(titleRef.current ?? panelRef.current);
    }, CASHBACK_CONFETTI_DELAY_MS);
    return () => window.clearTimeout(timeout);
  }, [fireConfetti, open]);

  const handleRedeem = () => {
    track('cashbackGrowthRedeem', { orderCode });
    window.open(
      buildCashbackWhatsappUrl({
        buyerFirstName,
        sellerName,
        orderCode,
        amountCents,
      }),
      '_blank',
      'noopener',
    );
    onDismiss();
  };

  const handleDismiss = () => {
    track('cashbackGrowthDismiss', { orderCode });
    onDismiss();
  };

  return {
    panelRef,
    titleRef,
    handleRedeem,
    handleDismiss,
  };
};
